import { createSelector } from 'reselect'
import { getAccommodationItems } from 'checkout/selectors/view/accommodation'
import { isSpoofed } from 'selectors/featuresSelectors'
import { getBookingCabinPricingData } from 'checkout/selectors/cruiseSelectors'
import {
  isTourV1Item,
  isTourV2Item,
  isCruiseItem,
  getTourV2IdForCheckoutItem,
  isBedbankItem,
  isLEHotelItem,
  isBNBLLEHotelItem,
  isInstantBookingLEHotelItem,
} from 'lib/checkout/checkoutUtils'
import { depositDisabled, getTourDepositInfo, getCruiseDepositInfo, getTourV2DepositInfo, getLeHotelDepositInfo } from 'lib/payment/depositsUtils'
import { isTourV2Offer } from 'lib/offer/offerTypes'
import { getCruiseDeposits } from 'checkout/lib/utils/cruises/booking'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'
import config from 'constants/config'

const getItemsDepositInfo = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => state.offer.offers,
  (state: App.State) => getBookingCabinPricingData(state),
  (state: App.State) => state.offer.offerAvailableRates,
  (state: App.State) => state.offer.tourV2Offers,
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => isSpoofed(state),
  (accommodationItems, offers, cabinPricingData, offersAvailableRates, tourV2Offers, isGift, isSpoofed): Array<App.Checkout.ItemDepositInfo> => {
    return accommodationItems
      .map((item) => {
        // Gift items should never be Deposit orders
        if (isGift) {
          return depositDisabled(item)
        }

        if (isTourV1Item(item)) {
          const offer = offers[item.offerId]
          return getTourDepositInfo(item, offer)
        }
        if (isTourV2Item(item)) {
          const offer = tourV2Offers[getTourV2IdForCheckoutItem(item)] ?? offers[getTourV2IdForCheckoutItem(item)]
          return isTourV2Offer(offer) ? getTourV2DepositInfo(item, offer) : depositDisabled(item)
        }

        if (isCruiseItem(item) && cabinPricingData.hasCabinPricing) {
          const paymentSchedule = getCruiseDeposits(cabinPricingData)
          if (paymentSchedule?.schedule.finalPayment) return getCruiseDepositInfo(item)
        }

        if (isBedbankItem(item)) {
          if (item?.bundledItemIds?.length) {
            return { itemId: item.itemId }
          }
          return depositDisabled(item)
        }

        if (!isLEHotelItem(item)) {
          return depositDisabled(item)
        }

        // Disable deposits if specified at an offer level
        if (offers[item.offerId]?.disableDeposit) {
          return depositDisabled(item)
        }

        if (isInstantBookingLEHotelItem(item)) {
          const offer = offers[item.offerId]
          const availableRatesKey = buildAvailableRateKey(item.checkIn, item.checkOut, [item.occupancy])
          // all available room rates, using only if flag useDynamicCancellationPolicies is true
          const offerAvailableRates = offersAvailableRates?.[item.offerId]?.[availableRatesKey]
          return offer ? getLeHotelDepositInfo(item, offer, offerAvailableRates) : depositDisabled(item)
        }

        if (isBNBLLEHotelItem(item) && !config.BNBL_DEPOSITS_ENABLED && !isSpoofed) {
          return depositDisabled(item)
        }

        // multi-room should be disabled for BNBL for deposits
        if (accommodationItems.filter(item => isBNBLLEHotelItem(item)).length > 1) {
          return depositDisabled(item)
        }

        // BNBL items, have no dates
        return {
          itemId: item.itemId,
        }
      })
  },
)

export default getItemsDepositInfo
